import { useRef, useState } from "react";

import { BookkeepingCategoryModal } from "@bokio/bookkeeping/src/components/BookkeepingCategoryModal/BookkeepingCategoryModal";
import { toValidatedLocalRoute } from "@bokio/bookkeeping/src/utils/localRoute";
import { GlobalFileDropZone } from "@bokio/components/GlobalFileDropZone/GlobalFileDropZone";
import { UploadReceiptProvider } from "@bokio/components/UploadReceiptProvider/UploadReceiptProvider";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useRouter } from "@bokio/shared/containers/router/useRouter";
import { getRoute } from "@bokio/shared/route";
import { useCompanyUser, useTodoStatus } from "@bokio/shared/state/requests";

import { useTopLevelUser } from "../TopLevelUserContext/useTopLevelUser";
import { UploadContext } from "./UploadContext";

import type { UploadContextType, UploadMessage } from "./UploadContext";
import type { Listener } from "@bokio/shared/services/api/signalRHub";
import type { FC, PropsWithChildren } from "react";

export const UploadContextProvider: FC<PropsWithChildren> = props => {
	const { company } = useCompanyUser();
	const { memberships } = useTopLevelUser();

	const { redirect } = useRouter();
	const listeners = useRef<Listener<UploadMessage>[]>([]);
	const { updateTodoStatus } = useTodoStatus();

	const [globalFileDropZoneBlockerCounter, setGlobalFileDropZoneBlockerCounter] = useState(0);
	const [categoryModalReceiptId, setCategoryModalReceiptId] = useState<string | undefined>();
	const [uploadError, setUploadError] = useState<string | undefined>();
	const globalFileDropZoneDisabled = globalFileDropZoneBlockerCounter !== 0;

	const messageQueue = useRef<UploadMessage[]>([]);
	const rerouteToBookkeepingOnSingleUpload = useRef<boolean | undefined>(false);

	const addListener = (listener: Listener<UploadMessage>) => {
		listeners.current = [...listeners.current, listener];
		messageQueue.current.forEach(e => listener(e));
	};

	const removeListener = (listener: Listener<UploadMessage>) => {
		listeners.current = listeners.current.filter(keep => keep !== listener);
	};

	const dispatchMessage = (e: UploadMessage) => {
		messageQueue.current = [...messageQueue.current, { ...e, historic: true }];
		listeners.current.forEach(listener => listener(e));
	};

	const isPartnerAccountant = memberships?.partners.some(p => p.PartnerId === company?.PartnerStatus?.PartnerId);
	const companyId = company?.Id;

	const handleUploadStarted = (files: File[]) => {
		if (!company) {
			throw new Error("Invalid Company");
		}
		if (
			files.length > 1 ||
			!rerouteToBookkeepingOnSingleUpload.current ||
			(!isPartnerAccountant &&
				company.HasActivePartner &&
				company.PartnerStatus?.PartnerCompanyType === m.Entities.PartnerCompanyType.FullPartner)
		) {
			redirect(getRoute("uploads", { company: company.Id }));
		}
		dispatchMessage({ type: "started", file: files });
	};

	return (
		<UploadReceiptProvider
			companyId={companyId}
			allowMultiUpload
			onUploadStarted={handleUploadStarted}
			onUploadChange={requests => dispatchMessage({ type: "change", requests })}
			onUploadDone={request => dispatchMessage({ request: request, type: "done" })}
			onEmptyFileError={message => {
				setUploadError(message);
				if (company) {
					redirect(getRoute("uploads", { company: company.Id }));
				}
			}}
			onSingleReceiptUpload={request => {
				if (!company) {
					throw new Error("Invalid Company");
				}

				if (rerouteToBookkeepingOnSingleUpload.current && request.Data) {
					setCategoryModalReceiptId(request.Data.Id);
				}
			}}
			onAllUploadsDone={requests => {
				updateTodoStatus();
				dispatchMessage({ type: "allDone", requests });
			}}
		>
			{uploader => {
				const context: UploadContextType = {
					requests: uploader.requests,
					invokeUpload: (routeToPreBookkeep?: boolean) => {
						uploader.setImmediatelyDoReceiptPrediction(false);
						uploader.invokeUpload();
						rerouteToBookkeepingOnSingleUpload.current = routeToPreBookkeep;
					},
					// TODO Support multiple uploads
					addListener,
					removeListener,
					setGlobalFileDropZoneBlockerCounter,
					uploadError,
					clearUploadError: () => setUploadError(undefined),
				};
				return (
					<UploadContext value={context}>
						{props.children}
						{/* Placing GlobalFileDropZone as the next sibling of children, so the drop zone can be shown above anything in the app baseLayer without using z-index. */}
						<GlobalFileDropZone
							disabled={globalFileDropZoneDisabled}
							handleUploadFiles={files => {
								uploader.setImmediatelyDoReceiptPrediction(false);
								uploader.handleUploadFiles(files);
								rerouteToBookkeepingOnSingleUpload.current = false;
							}}
						/>
						{categoryModalReceiptId && (
							<BookkeepingCategoryModal
								visible={!!categoryModalReceiptId}
								onClose={() => setCategoryModalReceiptId(undefined)}
								onSuccess={() => setCategoryModalReceiptId(undefined)}
								receiptId={categoryModalReceiptId}
								returnToRoute={
									company?.Id ? toValidatedLocalRoute(getRoute("uploads", { company: company.Id })) : undefined
								}
							/>
						)}
					</UploadContext>
				);
			}}
		</UploadReceiptProvider>
	);
};
